"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APosAdSlideCreateInputSchema = exports.APosAdSlideSchema = exports.APosAdSlideTextConfigSchema = exports.APosAdSlideImageConfigSchema = exports.APosAdAspectRatioSchema = exports.APosAdSlideImageOverlaySchema = exports.APosAdSlideVideoConfigSchema = exports.APosAdSlideProductConfigSchema = void 0;
const zod_1 = require("zod");
const timestamps_schema_1 = require("../../../common/timestamps.schema");
const id_schema_1 = require("../../../common/id.schema");
const enums_1 = require("./enums");
// ---------------------------------------------------------------------------
// Type-specific config blocks
// ---------------------------------------------------------------------------
exports.APosAdSlideProductConfigSchema = zod_1.z.object({
    productId: id_schema_1.EntityIdSchema,
    variantId: id_schema_1.EntityIdSchema.optional(),
    variantDisplay: zod_1.z.enum(['all', 'single']),
    priceEmphasis: zod_1.z.enum(['static', 'pulse', 'glow', 'bumpy', 'shake']),
    showPromotion: zod_1.z.boolean(),
    showCompliance: zod_1.z.boolean(),
    showLowStockBadge: zod_1.z.boolean(),
    theme: enums_1.APosAdThemeSchema,
    // ---- Additive: visibility toggles (default true at app level) ----
    showImage: zod_1.z.boolean().optional(),
    showName: zod_1.z.boolean().optional(),
    showPrice: zod_1.z.boolean().optional(),
    showVariants: zod_1.z.boolean().optional(),
    // ---- Additive: user-editable overrides ----
    customName: zod_1.z.string().max(200).optional(),
    customPrice: zod_1.z.string().max(50).optional(),
    customDescription: zod_1.z.string().max(300).optional(),
    // Override the auto-pulled promotion text (e.g. "BOGO weekend"). Renders next
    // to the strike-through price. When blank, viewer falls back to
    // activePromotion?.name.
    customPromotionText: zod_1.z.string().max(120).optional(),
    // Override the auto-pulled compliance text (e.g. "21+ ID required"). Renders
    // in the compliance badge. When blank, viewer derives from compliance.ageRequired.
    customComplianceText: zod_1.z.string().max(80).optional(),
    // ---- Additive: layout ----
    layout: zod_1.z.enum(['image-top', 'image-left', 'image-right', 'image-full', 'text-only']).optional(),
    textAlignment: zod_1.z.enum(['left', 'center', 'right']).optional(),
    // ---- Additive: text format ----
    nameSize: zod_1.z.enum(['sm', 'md', 'lg', 'xl', 'xxl']).optional(),
    priceSize: zod_1.z.enum(['sm', 'md', 'lg', 'xl', 'xxl']).optional(),
    // ---- Additive: colors (hex) ----
    nameColor: zod_1.z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    priceColor: zod_1.z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    // ---- Additive: image source selection ----
    // auto: variant image if a single variant is chosen, else product primary
    // specific: use selectedImageUrl
    // slideshow: cycle through all available images at slideshowIntervalSeconds
    imageMode: zod_1.z.enum(['auto', 'specific', 'slideshow']).optional(),
    selectedImageUrl: zod_1.z.string().url().optional(),
    slideshowIntervalSeconds: zod_1.z.number().int().min(1).max(30).optional(),
    // ---- Additive: custom CSS background — when set, viewer uses this and ignores `theme` ----
    themeBackground: zod_1.z.string().max(2000).optional(),
    // ---- Additive: explicit variant visibility allowlist ----
    // When variantDisplay === 'all' and this list is empty/undefined, ALL variants render.
    // When non-empty, only the listed variantIds render (in the order given).
    // Backwards-compatible: existing slides without this field keep showing all variants.
    visibleVariantIds: zod_1.z.array(zod_1.z.string()).optional(),
});
exports.APosAdSlideVideoConfigSchema = zod_1.z.object({
    source: zod_1.z.enum(['upload', 'url']),
    mediaId: id_schema_1.EntityIdSchema.optional(),
    externalUrl: zod_1.z.string().url().optional(),
    externalPlatform: zod_1.z.enum(['youtube', 'vimeo', 'direct']).optional(),
    muted: zod_1.z.boolean().default(true),
    loopWithinDuration: zod_1.z.boolean().default(true),
    fit: zod_1.z.enum(['cover', 'contain']),
}).refine((v) => (v.source === 'upload' ? !!v.mediaId : !!v.externalUrl), {
    message: "Video source 'upload' requires mediaId; source 'url' requires externalUrl",
    path: ['source'],
});
exports.APosAdSlideImageOverlaySchema = zod_1.z.object({
    // `text` is the primary field read by the TV viewer (overlay.text). The
    // editor mirrors it into `headline` for backwards compatibility with older
    // saved slides — both are optional.
    text: zod_1.z.string().max(200).optional(),
    headline: zod_1.z.string().max(100).optional(),
    subhead: zod_1.z.string().max(200).optional(),
    position: zod_1.z.enum([
        'top-left',
        'top-right',
        'top-center',
        'bottom-left',
        'bottom-right',
        'bottom-center',
        'center',
    ]),
    color: zod_1.z.string().min(1),
    shadow: zod_1.z.enum(['none', 'soft', 'hard']),
    animation: enums_1.APosAdAnimationSchema,
    // ---- Additive: font style + size (mirror TextConfig enums) ----
    // Both optional so existing slides keep validating; viewer/preview fall back
    // to sensible defaults when these are missing.
    fontStyle: zod_1.z.enum([
        'bold-huge',
        'serif-elegant',
        'script-playful',
        'mono-retro',
        'sans-light',
        'sans-condensed',
        'serif-bold',
        'mono-light',
        'wide-bold',
        'uppercase-bold',
    ]).optional(),
    size: zod_1.z.enum(['sm', 'md', 'lg', 'xl', 'xxl']).optional(),
    /** Optional CSS font-size (e.g. "4rem") — legacy field kept for back-compat. */
    fontSize: zod_1.z.string().max(50).optional(),
});
exports.APosAdAspectRatioSchema = zod_1.z.enum(['16:9', '4:3']);
exports.APosAdSlideImageConfigSchema = zod_1.z.object({
    mediaId: id_schema_1.EntityIdSchema,
    fit: zod_1.z.enum(['cover', 'contain']),
    aspectRatio: exports.APosAdAspectRatioSchema.default('16:9'),
    overlay: exports.APosAdSlideImageOverlaySchema.optional(),
});
exports.APosAdSlideTextConfigSchema = zod_1.z.object({
    headline: zod_1.z.string().min(1).max(100),
    body: zod_1.z.string().max(300).optional(),
    backgroundType: zod_1.z.enum(['solid', 'gradient', 'pattern']),
    backgroundValue: zod_1.z.string().min(1),
    aspectRatio: exports.APosAdAspectRatioSchema.default('16:9'),
    // ---- Additive: expanded fontStyle enum ----
    // The first 4 are the original values; the remaining 6 are new system-font
    // styles (no web font loading). Existing slides continue to validate.
    fontStyle: zod_1.z.enum([
        'bold-huge',
        'serif-elegant',
        'script-playful',
        'mono-retro',
        'sans-light',
        'sans-condensed',
        'serif-bold',
        'mono-light',
        'wide-bold',
        'uppercase-bold',
    ]),
    textColor: zod_1.z.string().min(1),
    textAnimation: enums_1.APosAdAnimationSchema,
    // ---- Additive: per-slide text size for headline and body ----
    // Maps to a clamp() font-size in the viewer and the preview.
    headlineSize: zod_1.z.enum(['sm', 'md', 'lg', 'xl', 'xxl']).optional(),
    bodySize: zod_1.z.enum(['sm', 'md', 'lg', 'xl', 'xxl']).optional(),
});
// ---------------------------------------------------------------------------
// Main slide schema
// ---------------------------------------------------------------------------
exports.APosAdSlideSchema = zod_1.z.object({
    tenantId: id_schema_1.TenantIdSchema,
    playlistId: id_schema_1.EntityIdSchema,
    slideId: id_schema_1.EntityIdSchema,
    slideType: enums_1.APosAdSlideTypeSchema,
    durationSeconds: zod_1.z.number().int().min(5).max(120),
    priority: enums_1.APosAdPrioritySchema.default('P2'),
    entryAnimation: enums_1.APosAdAnimationSchema.default('fade-in'),
    overlayEffect: enums_1.APosAdOverlayEffectSchema.default('none'),
    position: zod_1.z.number().int().nonnegative(),
    // When set to false, the slide is excluded from social-media video exports.
    // Default true at the app level — undefined means "include".
    includeInExport: zod_1.z.boolean().optional(),
    product: exports.APosAdSlideProductConfigSchema.optional(),
    video: exports.APosAdSlideVideoConfigSchema.optional(),
    image: exports.APosAdSlideImageConfigSchema.optional(),
    text: exports.APosAdSlideTextConfigSchema.optional(),
}).merge(timestamps_schema_1.TimestampsSchema).refine((s) => {
    switch (s.slideType) {
        case 'product':
            return !!s.product;
        case 'video':
            return !!s.video;
        case 'image':
            return !!s.image;
        case 'text':
            return !!s.text;
        default:
            return false;
    }
}, {
    message: 'Slide must include a config block matching its slideType (product/video/image/text)',
    path: ['slideType'],
});
// ---------------------------------------------------------------------------
// Create-input variants — relaxed so a draft slide can be created before
// the user has picked a product / uploaded media / pasted a URL.
// The UI populates these fields via subsequent PATCH calls. Storage schema
// (APosAdSlideSchema) stays strict for read paths.
// ---------------------------------------------------------------------------
const APosAdSlideProductCreateConfigSchema = zod_1.z.object({
    productId: zod_1.z.string(),
    variantId: zod_1.z.string().optional(),
    variantDisplay: zod_1.z.enum(['all', 'single']),
    priceEmphasis: zod_1.z.enum(['static', 'pulse', 'glow', 'bumpy', 'shake']),
    showPromotion: zod_1.z.boolean(),
    showCompliance: zod_1.z.boolean(),
    showLowStockBadge: zod_1.z.boolean(),
    theme: enums_1.APosAdThemeSchema,
    // ---- Additive: visibility toggles ----
    showImage: zod_1.z.boolean().optional(),
    showName: zod_1.z.boolean().optional(),
    showPrice: zod_1.z.boolean().optional(),
    showVariants: zod_1.z.boolean().optional(),
    // ---- Additive: user-editable overrides ----
    customName: zod_1.z.string().max(200).optional(),
    customPrice: zod_1.z.string().max(50).optional(),
    customDescription: zod_1.z.string().max(300).optional(),
    customPromotionText: zod_1.z.string().max(120).optional(),
    customComplianceText: zod_1.z.string().max(80).optional(),
    // ---- Additive: layout ----
    layout: zod_1.z.enum(['image-top', 'image-left', 'image-right', 'image-full', 'text-only']).optional(),
    textAlignment: zod_1.z.enum(['left', 'center', 'right']).optional(),
    // ---- Additive: text format ----
    nameSize: zod_1.z.enum(['sm', 'md', 'lg', 'xl', 'xxl']).optional(),
    priceSize: zod_1.z.enum(['sm', 'md', 'lg', 'xl', 'xxl']).optional(),
    // ---- Additive: colors (hex) ----
    nameColor: zod_1.z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    priceColor: zod_1.z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    // ---- Additive: image source selection ----
    imageMode: zod_1.z.enum(['auto', 'specific', 'slideshow']).optional(),
    selectedImageUrl: zod_1.z.string().url().optional(),
    slideshowIntervalSeconds: zod_1.z.number().int().min(1).max(30).optional(),
    // ---- Additive: custom CSS background ----
    themeBackground: zod_1.z.string().max(2000).optional(),
    // ---- Additive: explicit variant visibility allowlist (see APosAdSlideProductConfigSchema) ----
    visibleVariantIds: zod_1.z.array(zod_1.z.string()).optional(),
});
const APosAdSlideVideoCreateConfigSchema = zod_1.z.object({
    source: zod_1.z.enum(['upload', 'url']),
    mediaId: zod_1.z.string().optional(),
    externalUrl: zod_1.z.string().optional(),
    externalPlatform: zod_1.z.enum(['youtube', 'vimeo', 'direct']).optional(),
    muted: zod_1.z.boolean().default(true),
    loopWithinDuration: zod_1.z.boolean().default(true),
    fit: zod_1.z.enum(['cover', 'contain']),
});
const APosAdSlideImageCreateConfigSchema = zod_1.z.object({
    mediaId: zod_1.z.string(),
    fit: zod_1.z.enum(['cover', 'contain']),
    aspectRatio: exports.APosAdAspectRatioSchema.optional(),
    overlay: exports.APosAdSlideImageOverlaySchema.optional(),
});
/**
 * Create input — caller supplies slideType + duration + the matching type-specific
 * config. Server sets: tenantId, playlistId (from path), slideId, position, timestamps.
 * Priority and entryAnimation get defaults from the main schema.
 *
 * Uses *Create* config variants which accept empty IDs/URLs (draft slides);
 * the user fills them in via PATCH before the slide is shown on a TV.
 */
exports.APosAdSlideCreateInputSchema = zod_1.z.object({
    slideType: enums_1.APosAdSlideTypeSchema,
    durationSeconds: zod_1.z.number().int().min(5).max(120),
    priority: enums_1.APosAdPrioritySchema.optional(),
    entryAnimation: enums_1.APosAdAnimationSchema.optional(),
    overlayEffect: enums_1.APosAdOverlayEffectSchema.optional(),
    includeInExport: zod_1.z.boolean().optional(),
    product: APosAdSlideProductCreateConfigSchema.optional(),
    video: APosAdSlideVideoCreateConfigSchema.optional(),
    image: APosAdSlideImageCreateConfigSchema.optional(),
    text: exports.APosAdSlideTextConfigSchema.optional(),
}).refine((s) => {
    switch (s.slideType) {
        case 'product':
            return !!s.product;
        case 'video':
            return !!s.video;
        case 'image':
            return !!s.image;
        case 'text':
            return !!s.text;
        default:
            return false;
    }
}, {
    message: 'Slide create input must include a config block matching its slideType',
    path: ['slideType'],
});
//# sourceMappingURL=ad-slide.schema.js.map